import React, { useEffect, useState } from "react";
import Pool from "../../UserPool";
import purpleLogo from "../../assets/images/purpleKnowtexLogo.svg";
import { getOrgsDoctorsInfo } from "../../utils/public-admin-dashboard-functions";
import { format, set, subHours } from 'date-fns';
import ShowPatientDetails from "../OrgDoctorsView/ShowPatientDetails";
import { buildDoctorsCsv, escapeCsvValue } from "../OrgDoctorsView/buildDoctorsCsv";

const EXCLUDED_PATIENT_TYPES = new Set(['na', 'declined', 'cancelled']);

const getEmptyPatientSummary = () => ({
  cancelled: { total: 0, daily: 0 },
  declined: { total: 0, daily: 0 },
  na: { total: 0, daily: 0 },
});

const calculateScheduledPatients = (patients = []) => {
  return patients.filter(patient => {
    const type = patient.patient_type ? patient.patient_type.toLowerCase() : '';
    return !EXCLUDED_PATIENT_TYPES.has(type);
  }).length;
};

const normalizeScheduledPatientsCount = (doctor = {}) => {
  if (typeof doctor.scheduledPatientsToday === 'number') {
    return doctor.scheduledPatientsToday;
  }

  return calculateScheduledPatients(doctor.patients || []);
};

const defaultStarRating = { count: 0, average: null, details: [] };

export default function PublicHome({ setAuth, userOrg, setUserOrg }) {
  const [doctorName, setDoctorName] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [activePatients, setActivePatients] = useState([]);
  const [showLockedTooltip, setShowLockedTooltip] = useState(false);
  const [activeDoctorName, setActiveDoctorName] = useState("");
  const [totalOrgNotes, setTotalOrgNotes] = useState(0);
  const [totalDailyNotes, setTotalDailyNotes] = useState(0);
  const [totalScheduledPatients, setTotalScheduledPatients] = useState(0);
  const [totalLockedNotes, setTotalLockedNotes] = useState(0);
  const [patientTypeSummary, setPatientTypeSummary] = useState(getEmptyPatientSummary());
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [sortConfig, setSortConfig] = useState({
    key: "dailyNotes",
    direction: 'desc'
  });
  const [starRating, setStarRating] = useState(defaultStarRating);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const ratingDetails = starRating.details || [];
  const statGridRows = [
    [
      { label: "Total Notes", value: totalOrgNotes, key: "total-notes" },
      { label: "Daily Notes", value: totalDailyNotes, key: "daily-notes" }
    ],
    [
      { label: "Total N/A", value: patientTypeSummary.na.total, key: "total-na" },
      { label: "Daily N/A", value: patientTypeSummary.na.daily, key: "daily-na" }
    ],
    [
      { label: "Total Declined", value: patientTypeSummary.declined.total, key: "total-declined" },
      { label: "Daily Declined", value: patientTypeSummary.declined.daily, key: "daily-declined" }
    ],
    [
      { label: "Total Cancelled", value: patientTypeSummary.cancelled.total, key: "total-cancelled" },
      { label: "Daily Cancelled", value: patientTypeSummary.cancelled.daily, key: "daily-cancelled" }
    ],
    [
      { label: "Total Star Ratings", value: starRating.count || 0, key: "star-rating-count" },
      {
        label: "Average Star Rating",
        value: starRating.average !== null ? starRating.average : "N/A",
        key: "star-rating-average",
        onClick: () => {
          if (starRating.count > 0) setShowRatingModal(true);
        },
        clickable: starRating.count > 0
      }
    ]
  ];

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return (
        <svg className="w-4 h-4 inline-block ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const filteredDoctors = React.useMemo(() => {
    if (!doctorName) return doctors;

    return doctors.filter(doctor =>
      doctor.user_name.toLowerCase().includes(doctorName) ||
      doctor.user_email.toLowerCase().includes(doctorName)
    );
  }, [doctors, doctorName]);

  // Update the sortedDoctors logic to handle date fields
  const sortedDoctors = React.useMemo(() => {
    if (!sortConfig.key) return filteredDoctors;

    return [...filteredDoctors].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date fields specially
      if (sortConfig.key === 'recentNote' || sortConfig.key === 'firstNote') {
        // Convert "No note" to a date that sorts properly
        if (aValue === "No note") aValue = new Date(0); // Very old date
        else aValue = new Date(aValue);
        
        if (bValue === "No note") bValue = new Date(0); // Very old date  
        else bValue = new Date(bValue);
        
        // For date comparison
        if (sortConfig.direction === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }

      // Handle numeric fields
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        if (sortConfig.direction === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }

      // Handle text fields
      if (sortConfig.key === 'user_name' || sortConfig.key === 'user_email') {
        const aText = aValue || '';
        const bText = bValue || '';
        return sortConfig.direction === 'asc'
          ? aText.localeCompare(bText)
          : bText.localeCompare(aText);
      }

      // Default numeric comparison for other fields
      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  }, [filteredDoctors, sortConfig]);

  const todaysDate = format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    (async () => {
      if (!userOrg) return;
      setIsLoading(true);
      const orgData = await getOrgsDoctorsInfo(userOrg, selectedDate);
      const doctorList = orgData?.doctors || [];
      const typeSummary = orgData?.patientTypeSummary || getEmptyPatientSummary();
      const normalizedDoctors = doctorList.map(doctor => ({
        ...doctor,
        scheduledPatientsToday: normalizeScheduledPatientsCount(doctor)
      }));
      setDoctors(normalizedDoctors);
      setPatientTypeSummary({
        cancelled: {
          total: typeSummary.cancelled?.total || 0,
          daily: typeSummary.cancelled?.daily || 0
        },
        declined: {
          total: typeSummary.declined?.total || 0,
          daily: typeSummary.declined?.daily || 0
        },
        na: {
          total: typeSummary.na?.total || 0,
          daily: typeSummary.na?.daily || 0
        }
      });

      const ratingSummary = {
        count: orgData?.starRating?.count || 0,
        average: typeof orgData?.starRating?.average === 'number' ? orgData.starRating.average : null,
        details: orgData?.starRating?.details || []
      };
      setStarRating(ratingSummary);
      setShowRatingModal(false);

      if (normalizedDoctors && normalizedDoctors.length > 0) {
        const aggregateTotals = normalizedDoctors.reduce((acc, doctor) => {
          acc.totalNotes += doctor.totalNotes || 0;
          acc.dailyNotes += doctor.dailyNotes || 0;
          acc.scheduled += doctor.scheduledPatientsToday || 0;
          acc.locked += doctor.lockedNotes || 0;
          return acc;
        }, { totalNotes: 0, dailyNotes: 0, scheduled: 0, locked: 0 });
        setTotalOrgNotes(aggregateTotals.totalNotes);
        setTotalDailyNotes(aggregateTotals.dailyNotes);
        setTotalScheduledPatients(aggregateTotals.scheduled);
        setTotalLockedNotes(aggregateTotals.locked);
      } else {
        setTotalOrgNotes(0);
        setTotalDailyNotes(0);
        setTotalScheduledPatients(0);
        setTotalLockedNotes(0);
      }

      setIsLoading(false);
    })()
  }, [selectedDate, userOrg])

  const username = localStorage.getItem("username");

  const formattedDate = (isoDate) => {
    // Handle "No note" case
    if (!isoDate || isoDate === "No note") {
      return "No note";
    }

    try {
      // For date-only strings (yyyy-MM-dd), append T12:00:00 to ensure proper parsing
      let dateString = isoDate;
      if (typeof dateString === 'string' && dateString.length === 10 && dateString.includes('-')) {
        dateString = `${dateString}T12:00:00`;
      }

      // Create a Date object from the normalized string
      const dateObj = new Date(dateString);

      // Format the date
      return format(dateObj, 'MM/dd/yyyy');
    } catch (error) {
      console.error("Error formatting date:", error);
      return isoDate; // Return original if there's an error
    }
  };

  const formattedDateLong = (isoDate) => {
    // Handle "No note" case
    if (!isoDate || isoDate === "No note") {
      return "No note";
    }

    try {
      // For date-only strings (yyyy-MM-dd), append T12:00:00 to ensure proper parsing
      let dateString = isoDate;
      if (typeof dateString === 'string' && dateString.length === 10 && dateString.includes('-')) {
        dateString = `${dateString}T12:00:00`;
      }

      // Create a Date object from the normalized string
      const dateObj = new Date(dateString);

      // Format the date
      return format(dateObj, 'MMMM dd, yyyy');
    } catch (error) {
      console.error("Error formatting date:", error);
      return isoDate; // Return original if there's an error
    }
  };

  const logoutUser = () => {
    const user = Pool.getCurrentUser();

    if (user) {
      user.signOut()
    };
    setUserOrg(null);
    localStorage.removeItem("token");
    setAuth(false);
  };

  const handleDoctorQuery = (e) => {
    setDoctorName(e.target.value);
  }

  const handlePatientQuery = (patients, doctorName) => {
    setActiveDoctorName(doctorName);
    setActivePatients(patients);
    setShowPatientDetails(true);
  }

  const onClose = () => {
    setActivePatients([]);
    setActiveDoctorName("");
    setShowPatientDetails(false);
  }

  const handleReset = () => {
    setDoctorName("");
  }

  const handleExportCsv = () => {
    const csvContent = buildDoctorsCsv(sortedDoctors || []);
    const fileName = `${userOrg || 'doctors'}-${selectedDate}.csv`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const closeRatingModal = () => setShowRatingModal(false);
  const handleExportRatingDetails = () => {
    if (!ratingDetails.length) return;
    const headers = ["Doctor", "Date of Appt", "Rating", "Additional Feedback"];
    const rows = ratingDetails.map(detail => ([
      detail.doctor_name || '',
      detail.date_of_appt || '',
      detail.rating ?? '',
      detail.additional_feedback || ''
    ]));
    const csvContent = [headers, ...rows]
      .map(row => row.map(escapeCsvValue).join(','))
      .join('\n');
    const fileName = `${userOrg || 'org'}-ratings-${selectedDate}.csv`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full h-full flex flex-col gap-2 text-dark-purple font-sans">
      <div className="w-[90dvw] flex justify-between mx-auto mt-4">
        <img src={purpleLogo} className=""></img>
        <button className="flex hover:text-ktx-navy font-medium text-[24px] items-center gap-1" onClick={() => logoutUser()}>
          <svg className="w-6 h-6 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H8m12 0-4 4m4-4-4-4M9 4H7a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h2" />
          </svg>
          Logout
        </button>
      </div>
      <h1 className="font-sans ml-[5dvw] text-[36px] font-black mt-6">Welcome Back, {username}! 👋🏻</h1>
      <div className="flex justify-between items-center mx-[5dvw]">
        <h3 className="font-sans text-[24px] font-semibold tracking-[.1em]">{userOrg} At a Glance 🔎</h3>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <div className="grid grid-cols-2 gap-3">
            {statGridRows.map(row => (
              row.map(stat => {
                const isClickable = typeof stat.onClick === 'function' && stat.clickable;
                const cardClasses = [
                  "flex flex-col items-center px-3 py-1 bg-light-purple rounded-md min-w-[140px]",
                  stat.colSpan === 2 ? "col-span-2" : "",
                  isClickable ? "cursor-pointer hover:bg-ktx-navy hover:text-white transition-colors" : ""
                ].join(" ").trim();
                return (
                  <div
                    key={stat.key}
                    className={cardClasses}
                    onClick={isClickable ? stat.onClick : undefined}
                  >
                    <span className="text-lg font-bold text-ktx-purple">
                      {stat.value}
                    </span>
                    <span className="text-sm text-center whitespace-nowrap">{stat.label}</span>
                  </div>
                );
              })
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col w-[90dvw] mx-auto gap-4">
        <div className="flex justify-between items-center w-full">
          <div className="flex gap-4 w-[50%]">
            <div className="flex rounded-md bg-white shadow-knowtex-shadow w-full h-[2.5rem] gap-2">
              <svg className="self-center ml-[.4rem] w-6 h-6 text-ktx-navy dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
              </svg>
              <input
                type="text"
                placeholder="Search by doctor name or email"
                name="doctor name"
                required={true}
                className="text-ktx-navy p-[.5rem] w-full italic border-none bg-none rounded-md self-center"
                value={doctorName}
                onChange={handleDoctorQuery}
              />
            </div>
            <button className="font-semibold hover:bg-ktx-navy rounded-md bg-ktx-purple tracking-widest text-white py-[.7rem] h-[2.5rem] px-[3rem] items-center flex justify-center"
              onClick={handleReset}>Reset</button>
          </div>

          <div className="flex items-center gap-3">
            {/* DatePicker component */}
            <div className="flex items-center relative">
              <button
                onClick={(e) => {
                  const input = document.getElementById('date-picker-input');
                  input.style.position = 'absolute';
                  input.style.top = `0`;
                  input.style.right = '0';
                  input.style.opacity = '0';
                  input.showPicker();
                }}
                className="flex items-center gap-2 rounded-md border border-gray-200 p-2 shadow-knowtex-shadow bg-white hover:bg-gray-50"
              >
                <svg className="w-5 h-5 text-ktx-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-[16px] font-medium">{formattedDateLong(selectedDate)}</span>
              </button>
              <input
                id="date-picker-input"
                type="date"
                name="selectedDate"
                value={selectedDate}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value + 'T12:00:00');
                  setSelectedDate(format(selectedDate, 'yyyy-MM-dd'));
                }}
                className="absolute opacity-0 pointer-events-none"
              />
            </div>
            <button
              onClick={handleExportCsv}
              disabled={!sortedDoctors.length}
              className="font-semibold rounded-md bg-ktx-purple text-white py-2 px-4 shadow-knowtex-shadow hover:bg-ktx-navy disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Export to CSV
            </button>
          </div>
        </div>

        <div className="w-[90dvw] overflow-x-auto mx-auto mt-4">
          <div className="w-full max-h-[55dvh] relative mb-4 overflow-y-auto rounded-md shadow-knowtex-shadow">
            <table className="w-full bg-[#FBF9FF]">
              <thead>
                <tr className="py-2 px-4 sticky top-0 text-white z-10 bg-dark-purple">
                  <th
                    onClick={() => handleSort('user_name')}
                    className="px-4 py-2 text-left cursor-pointer hover:bg-ktx-navy"
                  >
                    Name {getSortIcon('user_name')}
                  </th>
                  <th
                    onClick={() => handleSort('user_email')}
                    className="px-4 py-2 text-left cursor-pointer hover:bg-ktx-navy"
                  >
                    Email {getSortIcon('user_email')}
                  </th>
                  <th
                    onClick={() => handleSort('scheduledPatientsToday')}
                    className="px-4 py-2 text-center cursor-pointer hover:bg-ktx-navy"
                  >
                    Scheduled Patients Today (no NA or Declined) {getSortIcon('scheduledPatientsToday')}
                  </th>
                  <th
                    onClick={() => handleSort('dailyNotes')}
                    className="px-4 py-2 text-center cursor-pointer hover:bg-ktx-navy"
                  >
                    Daily Notes {getSortIcon('dailyNotes')}
                  </th>
                  <th
                    onClick={() => handleSort('lockedNotes')}
                    className="px-4 py-2 text-center cursor-pointer hover:bg-ktx-navy"
                  >
                    <div className="flex items-center justify-center gap-1" onMouseLeave={() => setShowLockedTooltip(false)}>
                      Locked Notes
                      <div className="relative group">
                        <svg
                          className="w-5 h-5 text-white hover:text-yellow-400 transition-colors duration-200"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          onMouseEnter={() => setShowLockedTooltip(true)}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm0-14v4m0 4h.01"
                          />
                        </svg>
                        {showLockedTooltip && (
                          <span className="absolute top-[-4px] -translate-x-1/2 min-h-[2rem] flex items-center px-3 bg-sage-green text-white text-sm rounded-lg transition-opacity whitespace-nowrap">
                            Click on the numbers below to view note statuses
                          </span>
                        )}
                      </div>
                      {getSortIcon('lockedNotes')}
                    </div>
                  </th>
                  <th 
                    onClick={() => handleSort('recentNote')}
                    className="px-4 py-2 text-center cursor-pointer hover:bg-ktx-navy"
                  >
                    Most Recent {getSortIcon('recentNote')}
                  </th>
                  <th 
                    onClick={() => handleSort('firstNote')}
                    className="px-4 py-2 text-center cursor-pointer hover:bg-ktx-navy"
                  >
                    First Note {getSortIcon('firstNote')}
                  </th>
                  <th
                    onClick={() => handleSort('totalNotes')}
                    className="px-4 py-2 text-center cursor-pointer hover:bg-ktx-navy"
                  >
                    Total Notes {getSortIcon('totalNotes')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  [...Array(5)].map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      <td className="border-b px-4 py-2">
                        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      </td>
                      <td className="border-b px-4 py-2">
                        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      </td>
                  <td className="border-b px-4 py-2">
                    <div className="h-4 w-12 mx-auto bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="border-b px-4 py-2">
                    <div className="h-4 w-16 mx-auto bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="border-b px-4 py-2">
                    <div className="h-4 w-12 mx-auto bg-gray-200 rounded animate-pulse"></div>
                  </td>
                      <td className="border-b px-4 py-2">
                        <div className="h-4 w-12 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </td>
                      <td className="border-b px-4 py-2">
                        <div className="h-4 w-24 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </td>
                      <td className="border-b px-4 py-2">
                        <div className="h-4 w-24 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </td>
                    </tr>
                  ))
                ) : (sortedDoctors.map((doctor, index) => (
                  <tr onClick={() => handlePatientQuery(doctor.patients, doctor.user_name)} key={index} className="hover:bg-light-purple cursor-pointer">
                    <td className="border-b px-4 py-2">{doctor.user_name}</td>
                    <td className="border-b px-4 py-2 max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
                      {doctor.user_email}
                    </td>
                    <td className="border-b px-4 py-2 text-center">{doctor.scheduledPatientsToday ?? 0}</td>
                    <td className="border-b px-4 py-2 text-center">{doctor.dailyNotes}</td>
                    <td className="border-b px-4 py-2 text-center">{doctor.lockedNotes}</td>
                    <td className="border-b px-4 py-2 text-center">{doctor.recentNote === "No note" ? "No note" : formattedDate(doctor.recentNote)}</td>
                    <td className="border-b px-4 py-2 text-center">{doctor.firstNote === "No note" ? "No note" : formattedDate(doctor.firstNote)}</td>
                    <td className="border-b px-4 py-2 text-center">{doctor.totalNotes}</td>
                  </tr>
                )))}
              </tbody>
              <tfoot className="bg-white sticky bottom-0">
                <tr className="bg-light-purple font-semibold">
                  <td className="px-4 py-2 text-left" colSpan={2}>Daily Totals</td>
                  <td className="px-4 py-2 text-center">{totalScheduledPatients}</td>
                  <td className="px-4 py-2 text-center">{totalDailyNotes}</td>
                  <td className="px-4 py-2 text-center">{totalLockedNotes}</td>
                  <td className="px-4 py-2 text-center" colSpan={2}>—</td>
                  <td className="px-4 py-2 text-center">{totalOrgNotes}</td>
                </tr>
              </tfoot>
            </table>
            {showPatientDetails && <ShowPatientDetails patients={activePatients} doctorName={activeDoctorName} onClose={onClose} />}
          </div>
        </div>

        {showRatingModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-[80%] max-w-4xl max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <div>
                  <h2 className="text-2xl font-bold text-ktx-navy">Star Rating Feedback</h2>
                  <p className="text-sm text-gray-500">{userOrg} · {ratingDetails.length} entries</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExportRatingDetails}
                    disabled={!ratingDetails.length}
                    className="px-3 py-1 rounded-md bg-ktx-purple text-white font-semibold shadow disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Export CSV
                  </button>
                  <button onClick={closeRatingModal} className="text-gray-500 hover:text-ktx-navy">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto max-h-[70vh]">
                {ratingDetails.length === 0 ? (
                  <p className="text-center text-gray-500">No star ratings available.</p>
                ) : (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-light-purple text-ktx-navy">
                        <th className="px-4 py-2 text-left">Doctor</th>
                        <th className="px-4 py-2 text-left">Date of Appt</th>
                        <th className="px-4 py-2 text-left">Rating</th>
                        <th className="px-4 py-2 text-left">Additional Feedback</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ratingDetails.map((detail, index) => (
                        <tr key={`${detail.doctor_name}-${detail.date_of_appt}-${index}`} className="border-b">
                          <td className="px-4 py-2">{detail.doctor_name}</td>
                          <td className="px-4 py-2">{detail.date_of_appt}</td>
                          <td className="px-4 py-2">{detail.rating}</td>
                          <td className="px-4 py-2">{detail.additional_feedback || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
